import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, History, Users, LogOut, ShieldCheck, AlertTriangle, RotateCcw, BookOpen } from 'lucide-react';
import { useAgent } from '../App';
import { toast } from 'sonner';
import { desktopApi } from '../lib/desktopApi';
import { formatDateTime24 } from '../lib/time';
import ChangePassword from '../components/ChangePassword';
import { readLocalDraft } from '../lib/offlineStore';
import { reconcileTransferredDraft } from '../lib/syncEngine';
import SyncStatus from '../components/SyncStatus';
import DeleteDraftButton from '../components/DeleteDraftButton';

const Dashboard = () => {
  const navigate = useNavigate();
  const { currentAgent, logout } = useAgent();
  const [alertTitle, setAlertTitle] = useState('Service de Nuit Actif');
  const [alertMessage, setAlertMessage] = useState('');
  const [manoeuvres, setManoeuvres] = useState([]);
  const [draftInfo, setDraftInfo] = useState(null);
  const [transferNotice, setTransferNotice] = useState('');
  const [appInfo, setAppInfo] = useState(null);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const hostedSession = typeof window !== 'undefined' && !!localStorage.getItem('agentToken');
  const [establishment, setEstablishment] = useState({
    name: 'Vatel Security',
    subtitle: 'GK Sécurité',
    logo: '/gk-logo.png',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [titleRes, msgRes, manRes, draftRes, localDraft, appInfoRes, estNameRes, estSubtitleRes, logoRes] = await Promise.all([
          desktopApi.getSetting('alert_title').then((d) => ({ data: d })).catch(() => null),
          desktopApi.getSetting('alert_message').then((d) => ({ data: d })).catch(() => null),
          desktopApi.getManoeuvres().then((d) => ({ data: d })).catch(() => null),
          currentAgent?.id ? desktopApi.getReportDraft(currentAgent.id).catch(() => null) : Promise.resolve(null),
          currentAgent?.id ? readLocalDraft(currentAgent.id).catch(() => null) : Promise.resolve(null),
          desktopApi.getAppInfo().catch(() => null),
          desktopApi.getSetting('establishment_name').catch(() => null),
          desktopApi.getSetting('establishment_subtitle').catch(() => null),
          desktopApi.getSetting('logo_path').catch(() => null),
        ]);
        if (titleRes?.data?.value) setAlertTitle(titleRes.data.value);
        if (msgRes?.data?.value) setAlertMessage(msgRes.data.value);
        if (manRes?.data) setManoeuvres(manRes.data);
        const reportId = localDraft?.data?.id || draftRes?.data?.id || '';
        // Copie locale d'un rapport que le serveur ne garde plus pour cet agent :
        // s'il confirme le transfert, on la retire (seulement celle-ci).
        const moved = localDraft?.data?.id && !draftRes?.exists
          ? await reconcileTransferredDraft(currentAgent.id)
          : null;
        if (moved) {
          setTransferNotice(moved.message);
          setDraftInfo(null);
        } else if (localDraft?.data && (localDraft.pending || localDraft.pendingSubmit || !draftRes?.exists)) {
          setDraftInfo({
            exists: true,
            updated_at: localDraft.localUpdatedAt || localDraft.serverUpdatedAt,
            pending: !!(localDraft.pending || localDraft.pendingSubmit),
            reportId,
          });
        } else if (draftRes?.exists) setDraftInfo({ ...draftRes, reportId });
        else setDraftInfo(null);
        if (appInfoRes?.version) setAppInfo(appInfoRes);
        setEstablishment(prev => ({
          name: estNameRes?.value || prev.name,
          subtitle: estSubtitleRes?.value || prev.subtitle,
          logo: logoRes?.value || prev.logo,
        }));
      } catch (e) { /* keep defaults */ }
    };
    fetchData();
  }, [currentAgent?.id]);

  const handleLogout = async () => {
    const left = await logout();
    if (left === false) return;
    toast.success('Déconnecté avec succès');
    navigate('/');
  };

  const quickActions = [
    {
      id: 'new-report',
      title: 'Nouveau Rapport',
      description: 'Créer un rapport de nuit',
      icon: FileText,
      color: 'primary',
      path: '/nouveau-rapport'
    },
    {
      id: 'history',
      title: 'Historique',
      description: 'Consulter les rapports',
      icon: History,
      color: 'secondary',
      path: '/historique'
    },
    {
      id: 'consignes',
      title: 'Consignes',
      description: 'Aide-mémoire, check-in, règlement, étudiants',
      icon: BookOpen,
      color: 'secondary',
      path: '/consignes'
    },
    {
      id: 'admin',
      title: 'Administration',
      description: 'Gestion des agents',
      icon: Users,
      color: 'accent',
      path: '/admin'
    }
  ];

  return (
    <div className="safe-screen flex h-[100dvh] md:h-[calc(100vh-2.25rem)] overflow-hidden bg-background">
      {/* Sidebar */}
      <div className="w-64 hidden md:flex flex-col border-r border-border bg-card">
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <img src={establishment.logo} alt={establishment.name} className="w-10 h-10 rounded-lg" />
            <div>
              <h1 className="text-lg font-black tracking-tight uppercase">{establishment.name}</h1>
              <p className="text-xs text-muted-foreground">{establishment.subtitle}</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-2">
          <button
            onClick={() => navigate('/dashboard')}
            data-testid="nav-dashboard"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-md bg-primary/10 text-primary font-medium transition-colors"
          >
            <ShieldCheck className="w-5 h-5" />
            Accueil
          </button>
          <button
            onClick={() => navigate('/nouveau-rapport')}
            data-testid="nav-new-report"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors text-left"
          >
            <FileText className="w-5 h-5" />
            Nouveau Rapport
          </button>
          <button
            onClick={() => navigate('/historique')}
            data-testid="nav-history"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors text-left"
          >
            <History className="w-5 h-5" />
            Historique
          </button>
          <button
            onClick={() => navigate('/consignes')}
            data-testid="nav-consignes"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors text-left"
          >
            <BookOpen className="w-5 h-5" />
            Consignes
          </button>
          {currentAgent?.isAdmin && (
          <button
            onClick={() => navigate('/admin')}
            data-testid="nav-admin"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors text-left"
          >
            <Users className="w-5 h-5" />
            Administration
          </button>
          )}
        </nav>

        <div className="p-4 border-t border-border">
          <div className="bg-zinc-900/50 rounded-lg p-4 mb-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Agent en service</p>
            <p className="text-sm font-bold text-foreground">{currentAgent?.name}</p>
          </div>
          {hostedSession && (
            <button
              type="button"
              onClick={() => setShowPasswordForm((open) => !open)}
              className="mb-2 w-full h-11 rounded-md bg-secondary text-secondary-foreground text-sm font-semibold"
              data-testid="btn-own-password"
            >
              Mon mot de passe
            </button>
          )}
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-secondary hover:bg-secondary/80 text-secondary-foreground font-medium transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Déconnexion
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-6xl p-4 pb-[calc(8.5rem+env(safe-area-inset-bottom,0px))] md:p-10 md:pb-16">
        <div className="md:hidden flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-3 min-w-0">
            <img src={establishment.logo} alt="" className="w-10 h-10 rounded-lg shrink-0" />
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">En service</p>
              <p className="font-bold truncate">{currentAgent?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hostedSession && (
              <button
                type="button"
                onClick={() => setShowPasswordForm((open) => !open)}
                className="h-11 px-3 rounded-md bg-secondary text-secondary-foreground text-sm font-semibold"
              >
                Mot de passe
              </button>
            )}
            <button
              type="button"
              onClick={handleLogout}
              className="h-11 px-3 rounded-md bg-secondary text-secondary-foreground text-sm font-semibold inline-flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Sortir
            </button>
          </div>
        </div>
        <div className="mb-2 md:mb-8">
          <h1 className="text-4xl md:text-5xl tracking-tight uppercase font-bold mb-2">
            Accueil
          </h1>
          <p className="text-muted-foreground">Bienvenue {currentAgent?.name}</p>
          <div className="mt-3">
            <SyncStatus agentId={currentAgent?.id} />
          </div>
          {showPasswordForm && (
            <div className="mt-6 rounded-lg border border-border bg-card p-4">
              <ChangePassword onDone={() => setShowPasswordForm(false)} />
            </div>
          )}
        </div>

        {/* Alert Banner */}
        <div className="mb-2 md:mb-8 bg-gradient-to-b from-blue-500/10 to-transparent border border-primary/20 rounded-lg p-4 md:p-6">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-lg font-bold text-foreground mb-1">{alertTitle}</h3>
              <p className="text-sm text-muted-foreground">{alertMessage}</p>
            </div>
          </div>
        </div>

        {transferNotice && !draftInfo && (
          <div
            className="mb-8 rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground"
            data-testid="transferred-notice"
            role="status"
          >
            {transferNotice} Il n&apos;est plus modifiable sur cet appareil. Il reste lisible dans l&apos;historique.
          </div>
        )}

        {draftInfo && (
          <div className="mb-8 rounded-lg border border-primary/30 bg-primary/10 p-4 md:p-6">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="min-w-0">
                <h3 className="text-lg font-bold text-foreground uppercase tracking-wide flex items-center gap-2">
                  <RotateCcw className="w-5 h-5 text-primary flex-shrink-0" />
                  Rapport en cours
                </h3>
                <p className="text-sm text-muted-foreground mt-1 break-words">
                  {draftInfo.pending ? 'Brouillon sur cet appareil, en attente d\'envoi' : 'Brouillon sauvegardé'}
                  {draftInfo.updated_at && ` le ${formatDateTime24(draftInfo.updated_at, { dateStyle: 'short', timeStyle: 'short' })}`}
                </p>
              </div>
              <div className="flex w-full flex-col gap-2 sm:flex-row lg:w-auto">
                <button
                  onClick={() => navigate('/nouveau-rapport?resume=1')}
                  data-testid="btn-resume-draft"
                  className="w-full lg:w-auto h-12 px-6 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground font-semibold transition-colors whitespace-nowrap"
                >
                  Reprendre
                </button>
                <DeleteDraftButton
                  agentId={currentAgent?.id}
                  reportId={draftInfo.reportId || ''}
                  onDeleted={() => setDraftInfo(null)}
                  className="w-full lg:w-auto h-12 px-6 rounded-md bg-destructive hover:bg-destructive/90 text-destructive-foreground font-semibold transition-colors whitespace-nowrap"
                />
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2 md:gap-6">
          {quickActions.filter((action) => action.id !== 'admin' || currentAgent?.isAdmin).map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.id}
                onClick={() => navigate(action.path)}
                data-testid={`action-${action.id}`}
                className="group relative overflow-hidden rounded-xl border border-white/10 bg-zinc-900/50 p-4 md:p-6 min-h-20 hover:bg-zinc-900 transition-all duration-200 hover:border-primary/50 cursor-pointer text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:scale-95"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-foreground mb-1">{action.title}</h3>
                    <p className="text-sm text-muted-foreground">{action.description}</p>
                  </div>
                </div>
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-b from-blue-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </button>
            );
          })}
        </div>

        {/* Info Section */}
        <div className="mt-4 md:mt-12">
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-xl font-bold text-foreground mb-4 uppercase tracking-wide">Tâches de Nuit</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              {manoeuvres.length > 0 ? manoeuvres.map(m => (
                <li key={m.id} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary"></span>
                  {m.name}
                </li>
              )) : (
                <li className="text-muted-foreground">Aucune tâche configurée</li>
              )}
            </ul>
          </div>
        </div>
        {appInfo?.version && (
          <div className="mt-6 text-xs text-muted-foreground">
            Version {appInfo.version}
            {appInfo.changelog?.[0]?.changes?.[0] && ` - ${appInfo.changelog[0].changes[0]}`}
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default Dashboard;